import React, { useState, useEffect } from "react";
import { Globe, Shield, Lock, AlertTriangle, ArrowRight, Home, Mail, Phone, Clock, RefreshCcw } from "lucide-react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toggleLanguage } from "../redux/settings/settingsActions";
import { arClass } from "../utils/useArClass";
import { clearError } from "../redux/error/errorActions";

// Translation content
const translations = {
  en: {
    title: "Access Restricted",
    subtitle: "Geographical Access Limitation",
    description:
  "Access to this service is restricted in your region due to regulatory, compliance, and licensing requirements. We are currently unable to provide service availability from your location.",
    instruction:
      "If you believe this restriction is applied in error, please verify your location or contact our support team for further assistance.",
    support: "Support Center",
    homepage: "Return to Homepage",
    contact: "Contact Support",
    selectLanguage: "Select Language",
    errorCode: "Error Code: GEO-403",
    tryAgain: "Retry",
    footer: "© 2026 Raqib Health System. All rights reserved.",
    country: "Country",
    ip: "IP Address",
    available247: "Support available 24/7"
  },

  fr: {
    title: "Accès Restreint",
    subtitle: "Restriction d’Accès Géographique",
    description:
      "L’accès à ce service est restreint dans votre région en raison d’exigences réglementaires, de conformité et de licence. Nous ne sommes actuellement pas en mesure de fournir la disponibilité du service depuis votre emplacement.",
    instruction:
      "Si vous pensez que cette restriction est appliquée par erreur, veuillez vérifier votre localisation ou contacter notre équipe d’assistance.",
    support: "Centre de Support",
    homepage: "Retour à la Page d’Accueil",
    contact: "Contacter le Support",
    selectLanguage: "Choisir la Langue",
    errorCode: "Code d’Erreur : GEO-403",
    tryAgain: "Réessayer",
    footer: "© 2026 Raqib Health System. Tous droits réservés.",
    country: "Pays",
    ip: "Adresse IP",
    available247: "Support disponible 24h/24 et 7j/7"
  },

  ar: {
    title: "الوصول مقيد",
    subtitle: "تقييد الوصول الجغرافي",
    description:
      "الوصول إلى هذه الخدمة غير متاح حاليًا في منطقتك بسبب متطلبات الترخيص والامتثال واللوائح الإقليمية. نحن غير قادرين حاليًا على توفير الخدمة من موقعك.",
    instruction:
      "إذا كنت تعتقد أن هذا التقييد تم عن طريق الخطأ، يرجى التحقق من موقعك أو التواصل مع فريق الدعم للحصول على المساعدة.",
    support: "مركز الدعم",
    homepage: "العودة إلى الصفحة الرئيسية",
    contact: "التواصل مع الدعم",
    selectLanguage: "اختر اللغة",
    errorCode: "رمز الخطأ: GEO-403",
    tryAgain: "إعادة المحاولة",
    footer: "© 2026 Raqib Health System. جميع الحقوق محفوظة.",
    country: "الدولة",
    ip: "عنوان IP",
    available247: "الدعم متاح على مدار الساعة"
  }
};


const GeoErrorPage = () => {
  const language = useSelector((state) => state.settings.defaultLanguage);
  const darkMode = useSelector((state) => state.settings.darkMode);
  const ip = useSelector((state) => state.error.ip) || "";
  const country = useSelector((state) => state.error.country) || "";
  const dispatch = useDispatch();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const t = translations[language];
  const isRTL = language === "ar";
const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
const navigate = useNavigate();
  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    dispatch(toggleLanguage(selectedLanguage));
  };

  const handleRetry = () => {
    dispatch(clearError())
    isAuthenticated ? navigate("/dashboard") : navigate("/");
  }


  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div 
      className={`min-h-screen flex flex-col relative overflow-hidden ${
        darkMode ? 'bg-gray-950' : 'bg-gray-50'
      }`}
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 rounded-full bg-blue-500/10 blur-3xl"
          style={{
            left: `${mousePosition.x / 20}px`,
            top: `${mousePosition.y / 20}px`,
            transition: 'all 0.3s ease-out'
          }}
        />
        <div 
          className="absolute w-96 h-96 rounded-full bg-red-500/10 blur-3xl"
          style={{
            right: `${mousePosition.x / 30}px`,
            bottom: `${mousePosition.y / 30}px`,
            transition: 'all 0.3s ease-out'
          }}
        />
        
        
      </div>

      {/* Header */}
      <header className={`relative z-10 backdrop-blur-xl border-b ${
        darkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-white/50 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
              <img src="/black-logo.png" alt="Raqib Health Logo" className="w-6 h-6" />
       
            <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Raqib Health
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={language}
              onChange={handleLanguageChange}
              className={`px-4 py-2 max-sm:p-1.5 rounded-full border-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer max-sm:text-xs ${
                darkMode 
                  ? 'bg-gray-800/80 border-gray-700 text-white hover:bg-gray-800' 
                  : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
              }`}
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="ar">العربية</option>
            </select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-6 max-sm:p-2">
        <div className="max-w-6xl w-full">
          <div className={`rounded-3xl shadow-2xl overflow-hidden backdrop-blur-xl max-sm:rounded-xl ${
            darkMode ? 'bg-gray-900/80 border border-gray-800' : 'bg-white/90 border border-gray-200'
          }`}>
            <div className="p-8 md:p-12">
              <div className="grid lg:grid-cols-5 gap-12 items-center">
                {/* Left Column - Visual */}
                <div className="lg:col-span-2 flex flex-col items-center">
                  {/* Animated Globe */}
                  <div className="relative w-64 h-64 mb-8 max-sm:w-40 max-sm:h-40">
                    {/* Outer rotating ring */}
                    <div className="absolute inset-0 rounded-full border-2 border-dashed animate-spin-slow"
                      style={{
                        borderColor: darkMode ? '#1e40af' : '#3b82f6',
                        animationDuration: '20s'
                      }}
                    />
                    
                    {/* Middle ring */}
                    <div className="absolute inset-8 rounded-full border-2 border-dashed animate-spin-slower"
                      style={{
                        borderColor: darkMode ? '#7c2d12' : '#ef4444',
                        animationDuration: '15s',
                        animationDirection: 'reverse'
                      }}
                    />
                    
                    {/* Center shield */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-28 h-28 max-sm:w-14 max-sm:h-14 rounded-full bg-gradient-to-br from-red-500 via-orange-500 to-red-600 shadow-2xl flex items-center justify-center transform hover:scale-110 transition-transform duration-300">
                        <img src="/white-logo.png" alt="Raqib Health Logo" className="w-14 h-14 max-sm:w-10 max-sm:h-10" />
                      </div>
                    </div>
                    
                    {/* Floating warning icons */}
                    {[0, 120, 240].map((rotation, index) => (
                      <div
                        key={index}
                        className="absolute top-1/2 left-1/2 w-full h-full animate-orbit"
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          animationDelay: `${index * 0.3}s`,
                          animationDuration: '12s'
                        }}
                      >
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <div className={`p-2 rounded-lg ${
                            darkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'
                          }`}>
                            <AlertTriangle className="w-5 h-5 max-sm:w-4 max-sm:h-4 text-yellow-500" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Error Code Badge */}
                  <div className={arClass(`px-6 py-3 max-sm:px-4 max-sm:py-2 rounded-full font-bold text-sm max-sm:text-xs ${
                    darkMode 
                      ? 'bg-gradient-to-r from-red-900/40 to-orange-900/40 text-red-300 border border-red-800/50' 
                      : 'bg-gradient-to-r from-red-100 to-orange-100 text-red-700 border border-red-200'
                  }`)}>
                    {t.errorCode}
                  </div>
                </div>

                {/* Right Column - Content */}
                <div className="lg:col-span-3">
                  {/* Subtitle Badge */}
                  <div className={arClass("flex items-center mb-4")}>
                    <div className={arClass(`flex items-center gap-2 px-4 py-2 max-sm:px-2 max-sm:py-1 rounded-full ${
                      darkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
                    }`)}>
                      <Lock className={arClass(`w-4 h-4 max-sm:w-3 max-sm:h-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`)} />
                      <span className={arClass(`text-sm font-semibold max-sm:text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`)}>
                        {t.subtitle}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h1 className={`text-5xl md:text-6xl font-black pb-4 bg-gradient-to-r ${
                    darkMode 
                      ? 'from-white to-gray-400' 
                      : 'from-gray-900 to-gray-600'
                  } bg-clip-text text-transparent max-sm:text-lg`}>
                    {t.title}
                  </h1>

                  {/* Description */}
                  <p className={`text-lg mb-6 leading-relaxed ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  } max-sm:text-base`}>
                    {t.description}
                  </p>

                  {/* Info Box */}
                  <div className={`p-5 rounded-2xl mb-8 backdrop-blur-sm ${
                    darkMode 
                      ? 'bg-blue-500/5 border border-blue-500/20' 
                      : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg mt-0.5 ${
                        darkMode ? 'bg-yellow-500/10' : 'bg-yellow-100'
                      }`}>
                        <AlertTriangle className="w-5 h-5 max-sm:w-4 max-sm:h-4 text-yellow-500" />
                      </div>
                      <p className={darkMode ? 'text-gray-300 text-sm max-sm:text-xs' : 'text-gray-700 text-sm max-sm:text-xs'}>
                        {t.instruction}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className={arClass("flex flex-wrap gap-3 mb-8 max-sm:flex-col max-sm:gap-2")}>
                    <button
                      onClick={handleRetry}
                      className={arClass("group flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all transform hover:scale-105 max-sm:px-4 max-sm:py-2.5 max-sm:text-xs max-sm:flex-1")}
                    >
                      <span>{t.tryAgain}</span>
                      <RefreshCcw className={`w-5 h-5 max-sm:w-4 max-sm:h-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                    </button>

                    <a
                      href="mailto:support@raqibhealth.com"
                      className={arClass(`flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all transform hover:scale-105 max-sm:px-4 max-sm:py-2.5 max-sm:text-xs max-sm:flex-1 ${
                        darkMode 
                          ? 'bg-transparent hover:bg-gray-800 text-blue-400 border-2 border-blue-500/30 hover:border-blue-500/50' 
                          : 'bg-transparent hover:bg-blue-50 text-blue-600 border-2 border-blue-300 hover:border-blue-400'
                      }`)}
                    >
                      <Mail className={arClass("w-5 h-5 max-sm:w-4 max-sm:h-4")} />
                      <span>{t.contact}</span>
                    </a>
                  </div>

                  {/* Support Info */}
                  <div className={arClass(`p-6 rounded-2xl max-sm:px-4 max-sm:py-2.5 max-sm:text-xs max-sm:flex-1 ${
                    darkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-100 border border-gray-200'
                  }`)}>
                    <h3 className={arClass("font-bold text-lg mb-4 flex items-center gap-2 max-sm:text-sm")}>
                      <div className={arClass(`p-2 rounded-lg ${
                        darkMode ? 'bg-blue-500/10' : 'bg-blue-100'
                      }`)}>
                        <Phone className={arClass(`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`)} />
                      </div>
                      {t.support}
                    </h3>
                    <div className={arClass("grid sm:grid-cols-2 gap-3")}>
                      <div className={arClass("flex items-center gap-3")}>
                        <Mail className={arClass(`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`)} />
                        <span className={arClass("text-sm max-sm:text-xs")}>support@raqibhealth.com</span>
                      </div>
                      <div className={arClass("flex items-center gap-3")}>
                        <Phone className={arClass(`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`)} />
                        <span className={arClass("text-sm max-sm:text-xs")}>+1 (555) 123-4567</span>
                      </div>
                      <div className={arClass("flex items-center gap-3 sm:col-span-2")}>
                        <Clock className={arClass(`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`)} />
                        <span className={arClass("text-sm max-sm:text-xs")}>{t.available247}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={`relative z-10 backdrop-blur-xl border-t ${
        darkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-white/50 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-6 text-center">
          <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.footer}
          </p>
          <div className={arClass(`flex justify-center items-center gap-4 text-xs ${
            darkMode ? 'text-gray-500' : 'text-gray-500'
          }`)}>
            <span className={arClass("text-sm max-sm:text-xs")}>{t.ip}: {ip}</span>
            <span>•</span>
            <span className={arClass("text-sm max-sm:text-xs")}>{t.country}: {country}</span>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin-slow {
          to { transform: rotate(360deg); }
        }
        @keyframes spin-slower {
          to { transform: rotate(-360deg); }
        }
        @keyframes orbit {
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        .animate-spin-slower {
          animation: spin-slower 15s linear infinite;
        }
        .animate-orbit {
          animation: orbit 12s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default GeoErrorPage;